import { useEffect, useState } from "react";
import { X, Eye, EyeOff, Check, AlertCircle, ShieldAlert } from "lucide-react";
import api from "../../connector/api";
import toast from "react-hot-toast";
import useLogout from "../../hooks/useLogout";

export default function ChangePasswordModal({ isOpen, onClose, user }) {
  const [formData, setFormData] = useState({
    old_password: "",
    new_password: "",
    confirm_password: "",
  });
  
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    confirm: false,
  });
  
  const [criteria, setCriteria] = useState({
    length: false,
    noUsername: true,
    matches: false,
  });
  
  const [serverError, setServerError] = useState("");
  const logout = useLogout();

  useEffect(() => {
    const hasLength = formData.new_password.length >= 6;
    
    const excludesUsername = user?.username && formData.new_password
      ? !formData.new_password.toLowerCase().includes(user.username.toLowerCase())
      : true;
      
    const doesMatch = formData.new_password.length > 0 
      ? formData.new_password === formData.confirm_password 
      : false;

    setCriteria({
      length: hasLength,
      noUsername: excludesUsername,
      matches: doesMatch,
    });
  }, [formData, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (serverError) setServerError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!criteria.length || !criteria.noUsername || !criteria.matches) {
      toast.error("Please satisfy all validation criteria.");
      return;
    }

    setLoading(true);
    try {
      await api.post("/accounts/change-password/", formData);
      toast.success("Security token successfully rotated. Logging out...");
      logout();
    } catch (err) {
      console.error(err.response?.data || err.message);
      setServerError(err.response?.data?.detail || "Credential modification sequence failed.");
      toast.error("Update failed.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const fields = [
    { id: "old", name: "old_password", label: "Current Access Key" },
    { id: "new", name: "new_password", label: "New Access Key" },
    { id: "confirm", name: "confirm_password", label: "Verify New Access Key" },
  ];

  return (
    <div className="fixed inset-0 bg-slate-950/20 font-dynamic-title backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-in fade-in duration-100">
      <div className="bg-white border border-slate-200 rounded-lg shadow-xl w-full max-w-sm overflow-hidden animate-in zoom-in-98 duration-150">
        
        {/* Header Segment */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between border-b border-slate-100">
          <div>
            <h3 className="text-sm font-bold text-slate-900 tracking-tight mt-0.5">Rotate Access Key</h3>
          </div>
          <button 
            onClick={onClose} 
            className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form Container */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          
          {/* Inputs Loop */}
          <div className="space-y-4">
            {fields.map((field) => (
              <div key={field.id} className="space-y-1 relative">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                  {field.label}
                </label>
                <div className="relative rounded-xl">
                  <input
                    type={showPassword[field.id] ? "text" : "password"}
                    name={field.name}
                    value={formData[field.name]}
                    onChange={handleChange}
                    className="w-full bg-slate-50/50 border border-slate-200 focus:border-slate-900 focus:bg-white rounded-xl px-3.5 py-2 text-xs font-mono text-slate-800 placeholder-slate-300 transition-all outline-none pr-10"
                    required
                  />
                  <button
                    type="button"
                    tabIndex="-1"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 cursor-pointer"
                    onClick={() => setShowPassword((prev) => ({ ...prev, [field.id]: !prev[field.id] }))}
                  >
                    {showPassword[field.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Minimal Criteria Matrix */}
          <div className="p-4 bg-slate-50 border border-slate-200/60 rounded-2xl space-y-2.5">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block">Validation Requirements</span>
            
            <div className="space-y-1.5 text-xs">
              <div className={`flex items-center gap-2 transition-colors duration-150 ${criteria.length ? "text-emerald-700 font-semibold" : "text-slate-400"}`}>
                <div className={`w-3.5 h-3.5 rounded-md flex items-center justify-center border transition-all ${criteria.length ? "bg-emerald-50 border-emerald-200 text-emerald-600" : "bg-white border-slate-200"}`}>
                  <Check className={`w-2.5 h-2.5 transition-transform ${criteria.length ? "scale-100" : "scale-0"}`} />
                </div>
                <span className="text-[11px]">Minimum of 6 alpha-numeric slots</span>
              </div>

              <div className={`flex items-center gap-2 transition-colors duration-150 ${criteria.noUsername ? "text-emerald-700 font-semibold" : "text-rose-600 font-semibold"}`}>
                <div className={`w-3.5 h-3.5 rounded-md flex items-center justify-center border transition-all ${criteria.noUsername ? (formData.new_password ? "bg-emerald-50 border-emerald-200 text-emerald-600" : "bg-white border-slate-200") : "bg-rose-50 border-rose-200 text-rose-500"}`}>
                  {criteria.noUsername ? (
                    <Check className={`w-2.5 h-2.5 transition-transform ${formData.new_password ? "scale-100" : "scale-0"}`} />
                  ) : (
                    <AlertCircle className="w-2.5 h-2.5" />
                  )}
                </div>
                <span className="text-[11px]">Must isolate completely from login identifier</span>
              </div>

              <div className={`flex items-center gap-2 transition-colors duration-150 ${criteria.matches ? "text-emerald-700 font-semibold" : "text-slate-400"}`}>
                <div className={`w-3.5 h-3.5 rounded-md flex items-center justify-center border transition-all ${criteria.matches ? "bg-emerald-50 border-emerald-200 text-emerald-600" : "bg-white border-slate-200"}`}>
                  <Check className={`w-2.5 h-2.5 transition-transform ${criteria.matches ? "scale-100" : "scale-0"}`} />
                </div>
                <span className="text-[11px]">Identity verification fields coordinate perfectly</span>
              </div>
            </div>
          </div>

          {/* Exception Handler Panel */}
          {serverError && (
            <div className="p-3 bg-rose-50 border border-rose-100 rounded-xl text-rose-700 flex items-start gap-2 text-xs font-semibold">
              <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{serverError}</span>
            </div>
          )}

          {/* Action Blocks */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-200 text-slate-500 hover:text-slate-800 hover:bg-slate-50 font-bold text-[10px] tracking-widest font-mono rounded-xl transition cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !criteria.length || !criteria.noUsername || !criteria.matches}
              className={`flex-1 px-4 py-2 text-white font-bold text-[10px] tracking-widest font-mono rounded-xl shadow-xs transition cursor-pointer ${
                loading || !criteria.length || !criteria.noUsername || !criteria.matches
                  ? "bg-slate-100 text-slate-300 border border-slate-200/50 cursor-not-allowed shadow-none"
                  : "bg-[#002855] bg-opacity-80 hover:bg-opacity-80"
              }`}
            >
              {loading ? "Rotating..." : "Save Password"}
            </button>
          </div>
          
        </form>
      </div>
    </div>
  );
}